/**
 * The one classification the bivariate fill and its legend block share — spec §F5.4
 * (per-unit tertile classes → `BIV[yc][xc]`) and §F5.6(a) (the 3×3 grid).
 *
 * Third instance of the decision `choroClasses.ts` records (NOTES #190) and
 * `overlayClasses.ts` repeats (NOTES #283): a hook, not a store slice, because the map and
 * the legend must be describing the *same* tertile boundaries and the same nine colours,
 * and both already hold the same cached server state.
 *
 * The X half is not re-fetched: `useChoroplethClasses()` already resolves the base
 * indicator's values, the units FC, the year and the level, so this hook adds exactly one
 * query — the Y indicator's values, on the same `['values', id, level, year, country]` key
 * shape. That also means X's held-over-payload and level-mismatch guards apply unchanged.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  fetchIndicators,
  fetchValues,
  modeFor,
  queryKeys,
  type Indicator,
  type UnitsFC,
} from '../../lib/api';
import { selectYear, useAppStore, type ReportingLevel } from '../../lib/state';
import { bivariateClasses, FALLBACK_BIV } from '../../lib/symbology';
import { useChoroplethClasses } from './choroClasses';

/** One axis of the scheme, resolved. */
export interface BivVariable {
  id: string;
  /** §D3 catalogue row; null until `['indicators']` lands. */
  meta: Indicator | null;
  name: string;
  unitLabel: string;
  /** §G3 decimals. */
  fmt: number;
  /** unit_id → value, for the units in scope (no-data units are absent). */
  values: Map<string, number>;
  /** The two tertile upper bounds (§G5 `quantBreaks(vals,3)`). */
  breaks: number[];
}

export interface BivClasses {
  /** The base (fill) indicator — §F5.4 "keep base as X". */
  x: BivVariable;
  /** `biv.y`, the second pick. */
  y: BivVariable;
  /** `/meta/config.biv`, indexed `[yClass][xClass]`. */
  matrix: string[][];
  year: number;
  /** `0|1|2`, or `'watershed'` for the §M3.5 overlay-hierarchy mode. */
  level: ReportingLevel;
  units: UnitsFC | undefined;
  /** Both value payloads and the geometry landed. */
  ready: boolean;
  /** At least one unit in scope has a value on **both** axes. */
  hasData: boolean;
}

/**
 * Returns `null` unless §F5.4 is fully armed — bivariate on **and** a Y chosen. Until then
 * the single-indicator fill and legend block (b) stay exactly as they were, which is what
 * §F5.4's "keep base as X" means on screen while the reader is still picking.
 */
export function useBivariateClasses(): BivClasses | null {
  const bivOn = useAppStore((s) => s.biv.on);
  const yId = useAppStore((s) => s.biv.y);
  const level = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const cls = useChoroplethClasses();

  const on = bivOn && Boolean(yId) && Boolean(cls);

  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: on,
  });
  // Same `keepPreviousData` as the X side: §F5.11's "year change animates the choropleth"
  // has to hold for both halves of the fill, or the play loop strobes between the bivariate
  // paint and the single one.
  // The Y axis follows the same reading as the X one (`choroClasses`), narrowed by its own
  // catalogue row: a bivariate map whose two halves counted years differently would be
  // unreadable.
  const yMode = modeFor(cumulative, indicators?.find((i) => i.id === yId));
  const { data: rawY } = useQuery({
    queryKey: queryKeys.values(yId ?? '', level, year, country, yMode),
    queryFn: () => fetchValues(yId as string, level, year, country, yMode),
    enabled: on,
    placeholderData: keepPreviousData,
  });

  const yData = useMemo(
    () =>
      rawY && rawY.indicator.id === yId && (rawY.level === null || rawY.level === level)
        ? rawY
        : undefined,
    [rawY, yId, level],
  );

  const yValues = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of yData?.values ?? []) m.set(row.unit_id, row.value);
    return m;
  }, [yData]);

  const yMeta = useMemo(
    () => indicators?.find((i) => i.id === yId) ?? null,
    [indicators, yId],
  );

  return useMemo(() => {
    if (!bivOn || !yId || !cls) return null;

    // §F5.4 classifies "per-unit" over the displayed set, exactly as §F5.3 does — the
    // tertiles describe what is on screen, so they move with country and level.
    const xVals: number[] = [];
    const yVals: number[] = [];
    let both = 0;
    for (const f of cls.units?.features ?? []) {
      const xv = cls.values.get(f.properties.id);
      const yv = yValues.get(f.properties.id);
      const hasX = typeof xv === 'number' && Number.isFinite(xv);
      const hasY = typeof yv === 'number' && Number.isFinite(yv);
      if (hasX) xVals.push(xv);
      if (hasY) yVals.push(yv);
      if (hasX && hasY) both += 1;
    }

    // `quantBreaks` on an empty array yields `[undefined, undefined]`-shaped NaNs; guard
    // with a single zero so the legend still draws its nine cells over an empty year.
    const { xBreaks, yBreaks } = bivariateClasses(
      xVals.length ? xVals : [0],
      yVals.length ? yVals : [0],
    );

    return {
      x: {
        id: cls.indicatorId,
        meta: cls.meta,
        name: cls.meta?.name ?? cls.indicatorId,
        unitLabel: cls.unitLabel,
        fmt: cls.fmt,
        values: cls.values,
        breaks: xBreaks,
      },
      y: {
        id: yId,
        meta: yMeta,
        name: yMeta?.name ?? yId,
        unitLabel: yData?.indicator.unit ?? yMeta?.unit ?? '',
        fmt: yData?.indicator.fmt ?? yMeta?.fmt ?? 1,
        values: yValues,
        breaks: yBreaks,
      },
      matrix: cls.config?.biv?.length ? cls.config.biv : FALLBACK_BIV,
      // The year **on screen** — `cls.year` is already the held-over one during a fetch.
      year: cls.year,
      level: cls.level,
      units: cls.units,
      ready: cls.ready && Boolean(yData),
      hasData: both > 0,
    };
  }, [bivOn, yId, cls, yValues, yData, yMeta]);
}
